import React, { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Copy, Save, RotateCcw, ChevronRight, Package2, AlertCircle, CheckCircle2, Landmark, Search, Package, Droplets, Settings, Wallet, TrendingUp, Receipt, Sparkles, Lock, User, CreditCard, Truck, Phone, MapPin } from 'lucide-react';
import BrandSearchDropdown from '@/Components/shared/BrandSearchDropdown';
import { useNavigate, useLocation } from 'react-router-dom';
import KursBar from '@/components/hpp/KursBar';
import FinanceInvoiceCard from '@/components/hpp/FinanceInvoiceCard';
import BulkSampleInvoiceForm from '@/components/hpp/BulkSampleInvoiceForm';
import MarketingProductCard from '@/components/client-po/MarketingProductCard';
import FinanceLegalitasSection from '@/components/hpp/FinanceLegalitasSection';
import FinanceCustomItemCard from '@/components/hpp/FinanceCustomItemCard';
import BatchRepeatOrderPicker from '@/components/hpp/BatchRepeatOrderPicker';
import { useKurs } from '@/hooks/useKurs';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { format } from 'date-fns';
import PicSelector from '@/components/shared/PicSelector';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess } from '@/lib/AuthSession';
import { toast } from 'react-hot-toast';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import RepeatOrderScannerModal from '@/components/client-po/RepeatOrderScannerModal';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const getHrgJual = (item) => item?.harga_jual || 0;

const createEmptyProduct = () => ({
  nama_product: '',
  qty: '',
  botol_id: null,
  tutup_id: null,
  spray_id: null,
  ukuran_digunakan: 'label',
  racikan: [],
  jasa_nominal: undefined,
  komponen_tambahan: [],
  catatan: '',
  harga_jual_per_pcs: undefined,
  total_harga_jual: 0,
  total_hpp_product: 0,
  hpp_per_pcs_digunakan: 0,
});

export default function KalkulatorHPP() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isMarketing = location.pathname.includes('/marketing');
  const { kurs } = useKurs();
  const { createPO, clientPOs, updatePO } = useClientPOs();
  const editId = new URLSearchParams(location.search).get('edit');
  const editPO = React.useMemo(() => {
    if (!editId) return null;
    return clientPOs.find(p => 
      p.id === editId || 
      (editId.startsWith('INV-') && p.id === editId.replace(/^INV-/i, 'SPH-')) ||
      (editId.startsWith('SPH-') && p.id === editId.replace(/^SPH-/i, 'INV-'))
    );
  }, [editId, clientPOs]);

  const { member } = useSession();
  const [namaBrand, setNamaBrand] = useState('');
  const [penerimaKlien, setPenerimaKlien] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [tanggal, setTanggal] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [pic, setPic] = useState('');
  const [products, setProducts] = useState([createEmptyProduct()]);
  const [legalitas, setLegalitas] = useState([]);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [manualDeadline, setManualDeadline] = useState('');
  const [ppnMode, setPpnMode] = useState('include');
  const [ppnPercentage, setPpnPercentage] = useState(11);
  const [selectedBank, setSelectedBank] = useState('bca'); // Default to BCA
  const [poMode, setPoMode] = useState('custom'); // 'database' (Repeat Order) | 'custom' (Varian Baru) | 'sph' (Surat Penawaran Harga)
  const [sphSpesifikasi, setSphSpesifikasi] = useState(['Jenis Produk: Eau De Parfume', 'Semi Hard Box', '2 Varian aroma', 'Sablon Botol 1 Warna', 'Botol Kaca 30ml + Tutup'].join('\n'));
  const [sphTambahan, setSphTambahan] = useState(['Free sampel produk', 'Free R&D aroma', 'Jasa Produksi Sesuai Dengan CPKB'].join('\n'));
  const [sphTerms, setSphTerms] = useState(['Down Payment 50%, pelunasan 50% ketika selesai produksi dan barang siap dikirim', 'Belum termasuk ongkos kirim finished goods ke lokasi gudang costumer', 'Surat penawaran harga ini berlaku 3 (tiga) hari setelah diterbitkan.'].join('\n'));
  const [searchBrand, setSearchBrand] = useState('');
  const [ongkir, setOngkir] = useState(0);
  const [ongkirLabel, setOngkirLabel] = useState('Ongkos Kirim / Lainnya');
  const [paymentTerms, setPaymentTerms] = useState('cash'); // 'cash' | 'tempo'
  const [tempoDate, setTempoDate] = useState('');
  const [showScannerModal, setShowScannerModal] = useState(false);

  const accessKey = isMarketing ? 'marketing_invoices' : 'invoice_po';
  const canEdit = getPageAccess(member, accessKey) === 'edit' || getPageAccess(member, 'kalkulator_hpp') === 'edit';

  const { data: clientAccounts = [] } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });
  const { data: productionOrders = [] } = useQuery({ queryKey: ['production-orders'], queryFn: () => base44.entities.ProductionOrder.list() });

  const matchedClient = React.useMemo(() => {
    if (!namaBrand) return null;
    const clean = String(namaBrand).trim().toLowerCase();
    const matches = clientAccounts.filter(c => {
      if (c.status === 'deleted' || c.is_deleted) return false;
      const bName = (c.brand_name || '').trim().toLowerCase();
      const cName = (c.company_name || '').trim().toLowerCase();
      const cSplit = cName.split(' / ')[0].trim().toLowerCase();
      const inAssigned = Array.isArray(c.assigned_brands) && c.assigned_brands.some(b => b.trim().toLowerCase() === clean);
      return bName === clean || cName === clean || cSplit === clean || inAssigned;
    });

    if (matches.length === 0) return null;

    // Prioritaskan akun yang statusnya verified
    const verified = matches.filter(c => c.status === 'verified');
    const pool = verified.length > 0 ? verified : matches;

    // Prioritaskan akun yang memiliki owner_name atau alamat lengkap
    const sorted = [...pool].sort((a, b) => {
      const scoreA = (a.owner_name ? 10 : 0) + (a.addresses?.length ? 5 : 0) + (a.phone ? 2 : 0);
      const scoreB = (b.owner_name ? 10 : 0) + (b.addresses?.length ? 5 : 0) + (b.phone ? 2 : 0);
      return scoreB - scoreA;
    });

    return sorted[0];
  }, [namaBrand, clientAccounts]);

  const prevBrandRef = React.useRef('');

  React.useEffect(() => {
    if (namaBrand) {
      const cleanBrand = String(namaBrand).trim().toLowerCase();
      const isNewBrandSelected = prevBrandRef.current !== cleanBrand;
      prevBrandRef.current = cleanBrand;

      // 1. Dari Master Database ClientAccount
      if (matchedClient) {
        // Nama Owner / Penerima dari sisi Klien (Utamakan owner_name lalu addresses.penerima lalu pic_name jika bukan default role)
        const clientPenerima = matchedClient.owner_name || 
          matchedClient.addresses?.[0]?.penerima || 
          (!['admin', 'pic', 'client', 'admin kokulu'].includes((matchedClient.pic_name || '').toLowerCase()) ? matchedClient.pic_name : '');
        
        if (clientPenerima) {
          setPenerimaKlien(clientPenerima);
        } else if (isNewBrandSelected) {
          setPenerimaKlien('');
        }
        // PIC Marketing Satala (jika ada assigned_marketing di client account)
        if (matchedClient.assigned_marketing) {
          setPic(matchedClient.assigned_marketing);
        }
        if (matchedClient.phone || matchedClient.addresses?.[0]?.telepon) {
          setCustomerPhone(matchedClient.phone || matchedClient.addresses?.[0]?.telepon || '');
        }
        if (matchedClient.addresses && matchedClient.addresses.length > 0) {
          setCustomerAddress(matchedClient.addresses[0].alamat_lengkap || matchedClient.addresses[0].nama_alamat || '');
        } else if (matchedClient.location) {
          setCustomerAddress(matchedClient.location);
        }
        if (matchedClient.allowed_payment_terms && Array.isArray(matchedClient.allowed_payment_terms)) {
          if (matchedClient.allowed_payment_terms.includes('tempo') && !matchedClient.allowed_payment_terms.includes('cash')) {
            setPaymentTerms('tempo');
            const d = new Date(manualDeadline ? manualDeadline : deadlineDate);
            const days = Number(matchedClient.tempo_duration_days) || 30;
            d.setDate(d.getDate() + days);
            setTempoDate(format(d, 'yyyy-MM-dd'));
          } else if (matchedClient.allowed_payment_terms.includes('cash') && !matchedClient.allowed_payment_terms.includes('tempo')) {
            setPaymentTerms('dp50');
          }
        }
        // Rekening bank yang ditugaskan khusus klien
        const assignedBank = matchedClient.assigned_bank_account;
        const effectiveBank = (assignedBank && assignedBank !== 'all') ? assignedBank : selectedBank;
        if (assignedBank && assignedBank !== 'all' && assignedBank !== selectedBank) {
          setSelectedBank(assignedBank);
        }
        // Sinkronisasi status PPN: Klien override ('include'/'exclude') atau otomatis ikuti bank (ASB -> include, SDE -> exclude 11%)
        const clientPpnMode = matchedClient.default_ppn_mode || 'auto';
        if (clientPpnMode === 'include') {
          setPpnMode('include');
        } else if (clientPpnMode === 'exclude') {
          setPpnMode('exclude');
          setPpnPercentage(11);
        } else {
          // 'auto'
          if (effectiveBank === 'bni') {
            setPpnMode('exclude');
            setPpnPercentage(11);
          } else if (effectiveBank === 'bca') {
            setPpnMode('include');
          }
        }
      }

      // 2. Fallback: Cari dari transaksi PO sebelumnya di clientPOs
      const prevPO = clientPOs.find(p => {
        const pBrand = (p.customerName || p.namaBrand || p.brand_name || '').split(' / ')[0].trim().toLowerCase();
        return pBrand === cleanBrand && (p.customerAddress || p.phone);
      });
      if (prevPO) {
        if (!matchedClient?.owner_name && (isNewBrandSelected || !penerimaKlien)) {
          if (prevPO.penerima && prevPO.penerima !== '-' && prevPO.penerima !== prevPO.pic) {
            setPenerimaKlien(prevPO.penerima);
          } else if (prevPO.customerName?.includes(' / ')) {
            const candidate = prevPO.customerName.split(' / ')[1]?.trim();
            if (candidate && !['client', 'pic', 'asa', 'admin', 'muhammad fadli', cleanBrand].includes(candidate.toLowerCase()) && candidate !== prevPO.pic) {
              setPenerimaKlien(candidate);
            }
          }
        }
        // PIC Marketing Satala dari PO sebelumnya (jika belum terpilih)
        if (!pic && prevPO.pic) {
          setPic(prevPO.pic);
        }
        if ((isNewBrandSelected || !customerPhone) && prevPO.phone && prevPO.phone !== '-') {
          setCustomerPhone(prevPO.phone);
        }
        if ((isNewBrandSelected || !customerAddress) && prevPO.customerAddress && prevPO.customerAddress !== '-') {
          setCustomerAddress(prevPO.customerAddress);
        }
      }
    }
  }, [namaBrand, matchedClient, clientPOs]);

  const { data: rawBotolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const botolList = React.useMemo(() => [...rawBotolList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawBotolList]);

  const { data: rawTutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const tutupList = React.useMemo(() => [...rawTutupList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawTutupList]);

  const { data: rawSprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const sprayList = React.useMemo(() => [...rawSprayList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawSprayList]);

  const { data: rawBahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const bahanList = React.useMemo(() => [...rawBahanList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawBahanList]);

  const { data: rawItemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const itemTambahanList = React.useMemo(() => [...rawItemTambahanList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawItemTambahanList]);

  const { data: rawFullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const fullsetList = React.useMemo(() => [...rawFullsetList].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawFullsetList]);

  const { data: rawLegalitasListFinance = [] } = useQuery({ queryKey: ['legalitas'], queryFn: () => base44.entities.Legalitas.list() });
  const legalitasListFinance = React.useMemo(() => [...rawLegalitasListFinance].sort((a,b) => (a.nama||'').localeCompare(b.nama||'', 'id', { sensitivity: 'base' })), [rawLegalitasListFinance]);

  const { data: rawBibitSampleList = [] } = useQuery({ queryKey: ['bibit-sample'], queryFn: () => base44.entities.BibitSample.list() });
  const bibitSampleList = React.useMemo(() => [...rawBibitSampleList].sort((a,b) => (a.nama_bibit||'').localeCompare(b.nama_bibit||'', 'id', { sensitivity: 'base' })), [rawBibitSampleList]);

  const { accountsMap } = useBankAccounts();

  const mappedBibitSampleList = React.useMemo(() => {
    return bibitSampleList.map(b => ({
      ...b,
      nama: b.nama_bibit,
      kategori: 'Bibit',
      harga_rupiah: 0,
      harga_jual: 0,
      satuan: 'kg' // default satuan for bibit
    }));
  }, [bibitSampleList]);

  // Auto-fill Feature dari Database Brand Asli
  const { data: dbBrandsRaw = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list() });
  const { data: dbBahanRaw = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list() });

  // Ambil semua daftar brand unik dari database
  const allAvailableBrands = React.useMemo(() => {
    const brands = new Set();
    dbBrandsRaw.forEach(b => { if (b.nama_brand) brands.add(b.nama_brand); });
    dbBahanRaw.forEach(b => { if (b.nama_brand) brands.add(b.nama_brand); });
    return Array.from(brands).sort((a,b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));
  }, [dbBrandsRaw, dbBahanRaw]);

  const brandList = React.useMemo(() => {
    const map = {};
    if (!namaBrand) return []; // Hanya tampilkan varian jika brand sudah dipilih
    
    const filteredBrands = [namaBrand];
    
    // Process Racikan
    dbBrandsRaw.forEach(b => {
      if (!filteredBrands.some(fb => String(fb || '').toLowerCase() === String(b.nama_brand || '').toLowerCase())) return;
      
      let varianOnly = b.nama_varian || b.nama_variant || '';
      let sName = b.ukuran_ml ? String(b.ukuran_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1]; // Use the sub-item name as the unique size key
      } else if (varianOnly.includes(' - Item ')) {
        const parts = varianOnly.split(' - Item ');
        varianOnly = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      const vKey = `${b.nama_brand} - ${varianOnly}`;
      if (!map[vKey]) map[vKey] = { id: vKey, nama_product: varianOnly, sizes: {} };
      
      if (!map[vKey].sizes[joinKey]) map[vKey].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      map[vKey].sizes[joinKey].racikan = b;
    });

    // Process Bahan
    dbBahanRaw.forEach(b => {
      if (!filteredBrands.some(fb => String(fb || '').toLowerCase() === String(b.nama_brand || '').toLowerCase())) return;
      
      let varianOnly = b.nama_varian || b.nama_variant || '';
      let sName = b.botol_ukuran_label_ml ? String(b.botol_ukuran_label_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1];
      } else if (varianOnly.includes(' - Item ')) {
        const parts = varianOnly.split(' - Item ');
        varianOnly = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      const vKey = `${b.nama_brand} - ${varianOnly}`;
      if (!map[vKey]) map[vKey] = { id: vKey, nama_product: varianOnly, sizes: {} };
      
      if (!map[vKey].sizes[joinKey]) map[vKey].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      map[vKey].sizes[joinKey].bahan = b;
    });

    return Object.values(map).map(v => {
      const sizes = Object.values(v.sizes).sort((a, b) => (parseFloat(a.size) || 0) - (parseFloat(b.size) || 0)).map(s => {
        const b = s.bahan;
        const r = s.racikan;
        
        const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

        const isFullsetFromDB = Boolean(
          b?.is_fullset ||
          (b?.fullset_nama && b.fullset_nama.trim() !== '') ||
          (b?.tutup_nama && b.tutup_nama.toLowerCase().includes('fullset')) ||
          (b?.spray_nama && b.spray_nama.toLowerCase().includes('fullset'))
        );

        let matchedFullset = null;
        if (isFullsetFromDB || b?.botol_nama || b?.fullset_nama) {
          const targetNames = [b?.fullset_nama, b?.botol_nama].filter(Boolean);
          for (const target of targetNames) {
            const targetNorm = normalize(target);
            matchedFullset = fullsetList.find(x => normalize(x.nama) === targetNorm) ||
                             fullsetList.find(x => normalize(x.nama).includes(targetNorm) || targetNorm.includes(normalize(x.nama)));
            if (matchedFullset) break;
          }
        }

        const isActuallyFullset = Boolean(matchedFullset) || isFullsetFromDB;
        const matchedBotol = (b && !isActuallyFullset) ? botolList.find(x => (x.nama || '').trim().toLowerCase() === (b.botol_nama || '').trim().toLowerCase()) : null;
        const matchedTutup = (b && !isActuallyFullset) ? tutupList.find(x => (x.nama || '').trim().toLowerCase() === (b.tutup_nama || '').trim().toLowerCase()) : null;
        const matchedSpray = (b && !isActuallyFullset) ? sprayList.find(x => (x.nama || '').trim().toLowerCase() === (b.spray_nama || '').trim().toLowerCase()) : null;
        
        const mappedRacikan = (r?.racikan || []).map(rc => {
          const rcNama = rc.nama_bahan || rc.bahan_nama || '';
          const rcVendor = rc.vendor || '';

          let matchedIng = bahanList.find(
            x => (x.nama || '').trim().toLowerCase() === rcNama.trim().toLowerCase() &&
                 (rcVendor ? (x.vendor || '').trim().toLowerCase() === rcVendor.trim().toLowerCase() : true)
          );
          
          if (!matchedIng) {
            matchedIng = bahanList.find(
              x => (x.nama || '').trim().toLowerCase() === rcNama.trim().toLowerCase()
            );
          }

          if (!matchedIng) {
            const rcNorm = normalize(rcNama);
            matchedIng = bahanList.find(x => normalize(x.nama) === rcNorm && (rcVendor ? (x.vendor || '').trim().toLowerCase() === rcVendor.trim().toLowerCase() : true)) ||
                         bahanList.find(x => normalize(x.nama) === rcNorm);
          }
          
          // Fallback to match by category for standard base ingredients if name doesn't match
          const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain'];
          const catLower = (rc.kategori_bahan || rc.kategori || '').trim().toLowerCase();
          
          if (!matchedIng && stdCategories.includes(catLower)) {
            matchedIng = bahanList.find(x => (x.kategori || '').trim().toLowerCase() === catLower);
          }

          const fallbackId = matchedIng ? matchedIng.id : `fallback-${rcNama.replace(/\s+/g, '-')}`;
          const fallbackHargaBeli = rc.harga_per_satuan || rc.harga_beli || rc.harga_rupiah || 0;
          const fallbackHargaJual = rc.harga_jual_satuan || rc.harga_jual || (fallbackHargaBeli > 0 ? fallbackHargaBeli * 1.3 : 0);

          return {
            kategori: rc.kategori_bahan || rc.kategori || 'Bahan',
            bahan_id: fallbackId,
            bahan_nama: matchedIng ? matchedIng.nama : (stdCategories.includes(catLower) ? '' : rcNama),
            vendor: rc.vendor || (matchedIng ? matchedIng.vendor : null),
            grade: rc.grade || (matchedIng ? matchedIng.grade : null),
            persentase: rc.persentase || 0,
            satuan: matchedIng ? (matchedIng.satuan || 'kg') : (rc.satuan || 'kg'),
            harga_per_satuan: matchedIng ? (matchedIng.harga_rupiah || fallbackHargaBeli) : fallbackHargaBeli,
            harga_jual_satuan: matchedIng ? (matchedIng.harga_jual || (matchedIng.harga_rupiah * 1.3) || fallbackHargaJual) : fallbackHargaJual,
            custom_harga_per_unit: rc.custom_harga_per_unit || (rc.harga_jual ? (rc.harga_jual / 1000) : undefined)
          };
        });

        return {
          size: s.size,
          botol_id: matchedBotol ? matchedBotol.id : null,
          botol_nama: matchedBotol ? matchedBotol.nama : (b?.botol_nama || ''),
          tutup_id: matchedTutup ? matchedTutup.id : null,
          tutup_nama: matchedTutup ? matchedTutup.nama : (b?.tutup_nama || ''),
          spray_id: matchedSpray ? matchedSpray.id : null,
          spray_nama: matchedSpray ? matchedSpray.nama : (b?.spray_nama || ''),
          is_fullset: isActuallyFullset,
          fullset_id: matchedFullset ? matchedFullset.id : (b?.fullset_id || null),
          fullset_nama: matchedFullset ? matchedFullset.nama : (b?.fullset_nama || b?.botol_nama || null),
          botol_dari_client: !isActuallyFullset && (b?.botol_nama === 'Dari Client' || (!matchedBotol && !b?.botol_nama)),
          botol_ukuran_client: parseFloat(s.size) || 0,
          racikan: mappedRacikan,
          bahan: b ? { custom_jasa: b.custom_jasa, custom_margin: b.custom_margin, custom_harga_jual: b.custom_harga_jual, custom_hpp: b.custom_hpp } : null,
          komponen_tambahan: [...(b?.item_tambahan || []), ...(r?.item_tambahan || [])].map(it => ({
            nama: it.nama,
            kategori: it.kategori,
            harga: it.harga || 0,
            harga_jual: it.harga_jual || 0,
            qty: it.qty_per_pcs || it.qty || it.jumlah || 1
          }))
        };
      });

      return {
        ...v,
        sizes
      };
    }).sort((a, b) => (a.nama_product || '').localeCompare(b.nama_product || '', 'id', { sensitivity: 'base' }));
  }, [dbBrandsRaw, dbBahanRaw, namaBrand, botolList, tutupList, sprayList, bahanList, fullsetList]);

  // Daftar nama varian aktif: gabungkan dari form produk aktif + database brand terpilih
  const availableProductVariants = React.useMemo(() => {
    const list = [];
    (products || []).forEach((p, idx) => {
      const rawName = p.nama_product || p.custom_nama_produk || p.nama || '';
      const cleanName = rawName ? rawName.trim() : '';
      if (cleanName && !list.includes(cleanName)) {
        list.push(cleanName);
      }
    });
    // Sertakan juga seluruh varian dari database brand yang sedang dipilih
    (brandList || []).forEach(b => {
      const bName = (b.nama_product || '').trim();
      if (bName && !list.includes(bName)) {
        list.push(bName);
      }
    });
    return list;
  }, [products, brandList]);

  const handleProductChange = useCallback((index, field, value) => {
    setProducts(prev => {
      const newP = [...prev];
      if (typeof field === 'object' && field !== null) {
        newP[index] = { ...newP[index], ...field };
      } else {
        newP[index] = { ...newP[index], [field]: value };
      }
      return newP;
    });
  }, []);

  const updateProduct = useCallback((idx, data) => {
    setProducts(prev => prev.map((p, i) => i === idx ? { ...p, ...data } : p));
  }, []);

  const addProduct = () => setProducts(prev => [...prev, createEmptyProduct()]);

  const duplicateProductPackaging = useCallback((sourceIndex = null) => {
    setProducts(prev => {
      if (prev.length === 0) return [createEmptyProduct()];
      
      let targetIdx = sourceIndex;
      // Jika tidak ditentukan (tombol bawah), atau jika target kartu yang dipilih tidak memiliki racikan,
      // cari kartu paling akhir yang sudah memiliki racikan
      if (targetIdx === null || targetIdx === undefined || targetIdx < 0 || targetIdx >= prev.length) {
        const foundWithRacikan = [...prev].reverse().findIndex(p => Array.isArray(p.racikan) && p.racikan.length > 0);
        if (foundWithRacikan !== -1) {
          targetIdx = prev.length - 1 - foundWithRacikan;
        } else {
          targetIdx = prev.length - 1;
        }
      } else if (!prev[targetIdx]?.racikan || prev[targetIdx].racikan.length === 0) {
        // Jika kartu yang diklik duplikat tidak memiliki racikan, coba ambil racikan dari kartu lain yang punya
        const foundWithRacikan = [...prev].reverse().findIndex(p => Array.isArray(p.racikan) && p.racikan.length > 0);
        if (foundWithRacikan !== -1) {
          // Tetap gunakan kemasan dari targetIdx, tapi formula dari foundWithRacikan
          const sourceFormula = prev[prev.length - 1 - foundWithRacikan];
          if (sourceFormula && sourceFormula.racikan && sourceFormula.racikan.length > 0) {
            prev[targetIdx].racikan = JSON.parse(JSON.stringify(sourceFormula.racikan));
          }
        }
      }

      const source = prev[targetIdx] || prev[prev.length - 1];
      if (!source) return [...prev, createEmptyProduct()];

      const duplicated = {
        ...createEmptyProduct(),
        qty: source.qty || '',
        // Kemasan & Packaging
        botol_id: source.botol_id || null,
        tutup_id: source.tutup_id || null,
        spray_id: source.spray_id || null,
        is_fullset: source.is_fullset || false,
        fullset_id: source.fullset_id || null,
        botol_dari_client: source.botol_dari_client || false,
        botol_nama_client: source.botol_nama_client || '',
        botol_ukuran_client: source.botol_ukuran_client || 0,
        tutup_dari_client: source.tutup_dari_client || false,
        tutup_nama_client: source.tutup_nama_client || '',
        spray_dari_client: source.spray_dari_client || false,
        spray_nama_client: source.spray_nama_client || '',
        ukuran_digunakan: source.ukuran_digunakan || 'label',
        custom_ukuran_aktual_ml: source.custom_ukuran_aktual_ml,
        // Jasa & Margin
        jasa_nominal: source.jasa_nominal,
        custom_jasa: source.custom_jasa,
        custom_margin: source.custom_margin,
        custom_harga_jual: source.custom_harga_jual,
        // Komponen Tambahan (Dus/Box, Hardbox, Stiker, Kartu, dll.)
        komponen_tambahan: Array.isArray(source.komponen_tambahan)
          ? JSON.parse(JSON.stringify(source.komponen_tambahan))
          : [],
        // Nama varian dikosongkan agar diisi nama varian baru
        nama_product: '',
        // Racikan formula ikut tersalin: pelarut (Alkohol dll.) tetap utuh, baris bibit persentasenya terjaga & tinggal pilih wangi baru
        racikan: Array.isArray(source.racikan)
          ? source.racikan.map(r => {
              const kat = (r.kategori || r.kategori_bahan || '').toLowerCase();
              const isBibit = kat === 'bibit' || kat.includes('bibit') || kat.includes('fragrance');
              if (isBibit) {
                return {
                  ...r,
                  kategori: r.kategori || 'Bibit',
                  persentase: r.persentase || 0,
                  volume_ml: r.volume_ml || 0,
                  satuan: r.satuan || 'kg',
                  bahan_id: null,
                  bahan_nama: '',
                  vendor: '',
                  grade: '',
                  hpp_bahan: 0,
                  harga_per_satuan: 0,
                  custom_harga_per_unit: undefined,
                  dari_client: r.dari_client || false,
                };
              }
              return { ...r };
            })
          : [],
        catatan: '',
      };

      return [...prev, duplicated];
    });
    toast.success('Kemasan & komponen varian berhasil disalin! Silakan beri nama varian & racikan bibit baru.');
  }, []);

  const removeProduct = (idx) => setProducts(prev => prev.filter((_, i) => i !== idx));

  const handleAddBatchProducts = (newProductCards, replaceExisting) => {
    if (replaceExisting) {
      setProducts(newProductCards);
    } else {
      const cleanedCurrent = (products || []).filter(p => p.nama_product || (p.racikan && p.racikan.length > 0));
      setProducts([...cleanedCurrent, ...newProductCards]);
    }
  };

  const handleReset = () => {
    setNamaBrand('');
    setTanggal(format(new Date(), 'yyyy-MM-dd'));
    setPic('');
    setPenerimaKlien('');
    setCustomerPhone('');
    setCustomerAddress('');
    setProducts([createEmptyProduct()]);
    setLegalitas([]);
    setOngkir(0);
    setSaved(false);
    if (editId) navigate(location.pathname);
  };

  React.useEffect(() => {
    if (editPO && !saved) {
      const parts = (editPO.customerName || '').split(' / ');
      setNamaBrand(parts[0] || '');
      setCustomerPhone(editPO.phone && editPO.phone !== '-' ? editPO.phone : '');
      setCustomerAddress(editPO.customerAddress && editPO.customerAddress !== '-' ? editPO.customerAddress : '');
      // PIC Marketing Satala
      setPic(editPO.pic || editPO.pic_marketing || '');
      
      // PIC / Penerima dari sisi Klien
      if (editPO.penerima && editPO.penerima !== '-' && editPO.penerima.trim()) {
        setPenerimaKlien(editPO.penerima.trim());
      } else if (parts[1] && parts[1] !== 'Client' && parts[1] !== editPO.pic) {
        setPenerimaKlien(parts[1].trim());
      } else {
        setPenerimaKlien('');
      }
      
      setPoMode(editPO.poMode || (editPO.isSample ? 'sample' : 'custom')); // Use saved poMode or fallback

      // Load produk dan legalitas
      if (editPO.products && editPO.products.length > 0) {
        const loadedSorted = [...editPO.products].sort((a, b) => {
          const nameA = a.nama_product || '';
          const nameB = b.nama_product || '';
          if (!nameA && nameB) return 1;
          if (nameA && !nameB) return -1;
          return nameA.localeCompare(nameB, 'id');
        });
        setProducts(loadedSorted.map(p => {
          const loadedJasa = (p.jasa_nominal !== undefined && p.jasa_nominal !== null && p.jasa_nominal !== 0) ? p.jasa_nominal : p.final_jasa_nominal;
          const loadedHargaJual = (p.harga_jual_per_pcs !== undefined && p.harga_jual_per_pcs !== null && p.harga_jual_per_pcs !== 0) 
            ? p.harga_jual_per_pcs 
            : (p.final_harga_jual_per_pcs || (p.total_harga_jual ? p.total_harga_jual / (parseFloat(String(p.qty || 1).replace(',', '.'))) : undefined));

          // Re-hydrate racikan prices & vendor if 0 or missing
          const hydratedRacikan = (p.racikan || []).map(r => {
            const rNama = r.bahan_nama || r.nama_bahan || r.nama || '';
            const rVendor = r.vendor || '';
            const matched = bahanList.find(b => 
              (b.nama || '').trim().toLowerCase() === rNama.trim().toLowerCase() &&
              (rVendor ? (b.vendor || '').trim().toLowerCase() === rVendor.trim().toLowerCase() : true)
            ) || bahanList.find(b => 
              (b.nama || '').trim().toLowerCase() === rNama.trim().toLowerCase()
            );

            const hBeli = r.harga_per_satuan || r.harga_beli || r.harga_rupiah || (matched ? (matched.harga_rupiah || 0) : 0);
            const hJual = r.harga_jual_satuan || r.harga_jual || (matched ? (matched.harga_jual || (matched.harga_rupiah * 1.3) || 0) : 0);

            return {
              ...r,
              bahan_id: r.bahan_id || (matched ? matched.id : undefined),
              bahan_nama: rNama,
              vendor: r.vendor || (matched ? matched.vendor : ''),
              grade: r.grade || (matched ? matched.grade : ''),
              harga_per_satuan: hBeli,
              harga_jual_satuan: hJual
            };
          });

          return {
            ...p,
            racikan: hydratedRacikan,
            jasa_nominal: loadedJasa,
            harga_jual_per_pcs: loadedHargaJual
          };
        }));
      }
      if (editPO.legalitas) {
        setLegalitas(editPO.legalitas);
      }
      if (editPO.shippingCost !== undefined) {
        setOngkir(editPO.shippingCost);
      }
      if (editPO.shippingCostLabel) {
        setOngkirLabel(editPO.shippingCostLabel);
      }
      if (editPO.ppn_mode) {
        setPpnMode(editPO.ppn_mode);
        setPpnPercentage(editPO.ppn_percentage || 11);
      }
      if (editPO.paymentTerms) setPaymentTerms(editPO.paymentTerms);
      if (editPO.tempoDate) {
        try {
          let parsedTempo = new Date(editPO.tempoDate.replace(/Mei/i, 'May').replace(/Agt/i, 'Aug').replace(/Okt/i, 'Oct').replace(/Des/i, 'Dec').replace(/Nop/i, 'Nov'));
          if (!isNaN(parsedTempo.getTime())) setTempoDate(format(parsedTempo, 'yyyy-MM-dd'));
        } catch (e) {}
      }
      if (editPO.paymentMethod) {
          let pm = String(editPO.paymentMethod).toLowerCase();
          if (pm.includes('mandiri') || pm.includes('qris')) pm = 'mandiri';
          else if (pm.includes('bni')) pm = 'bni';
          else if (pm.includes('bca')) pm = 'bca';
          setSelectedBank(pm);
        }
      
      // Load tanggal
      try {
        if (editPO.date) {
          let dateStr = editPO.date.replace(/Mei/i, 'May').replace(/Agt/i, 'Aug').replace(/Okt/i, 'Oct').replace(/Des/i, 'Dec').replace(/Nop/i, 'Nov');
          const parsed = new Date(dateStr);
          if (!isNaN(parsed.getTime())) {
            setTanggal(format(parsed, 'yyyy-MM-dd'));
          } else if (editPO.created_date || editPO.created_at) {
            setTanggal(format(new Date(editPO.created_date || editPO.created_at), 'yyyy-MM-dd'));
          }
        } else if (editPO.created_date || editPO.created_at) {
          setTanggal(format(new Date(editPO.created_date || editPO.created_at), 'yyyy-MM-dd'));
        }
      } catch (e) {
        console.warn('Could not parse date for edit mode', e);
      }
    }
  }, [editPO, saved]);

  const totalLegalitasJual = ['custom', 'sph', 'custom_finance', 'database'].includes(poMode) ? legalitas.reduce((s, l) => s + (l.harga_jual || 0) * (l.qty_variant || (l.selected_variants?.length || 1)), 0) : 0;
  const activeProducts = products.filter(p => p.nama_product && p.nama_product.trim() !== '');
  const totalSemuaModal = activeProducts.reduce((s, p) => s + (p.total_hpp_product || 0), 0);
  const totalHargaJualProduk = activeProducts.reduce((s, p) => s + (p.total_harga_jual || 0), 0);
  const subtotalTanpaOngkir = totalHargaJualProduk + totalLegalitasJual;
  const nominalPPN = ppnMode === 'exclude' ? (subtotalTanpaOngkir * (parseFloat(ppnPercentage) || 0) / 100) : 0;
  const totalHargaJualSemua = subtotalTanpaOngkir + nominalPPN + (parseFloat(ongkir) || 0);
  const isProductsValid = activeProducts.length === 0 
    ? (legalitas.length > 0)
    : activeProducts.every(p => {
        if (poMode === 'custom_finance') return p.nama_product && p.nama_product.trim() !== '';
        const isDiscoverySet = p.nama_product && p.nama_product.toLowerCase().includes('discovery set');
        const totalPersen = (p.racikan || []).reduce((s, r) => s + (parseFloat(String(r.persentase).replace(',', '.')) || 0), 0);
        
        const vol = p.botol_ukuran_client || p.botol_ukuran_label_ml || p.botol_ukuran_aktual || p.ukuran_botol_ml || p.selected_size || p.custom_ukuran_ml || 0;
        const hasBotol = Boolean(p.botol_id || p.botol_nama || p.fullset_id || p.fullset_nama || p.is_fullset || p.botol_dari_client || vol > 0 || isDiscoverySet);
        const isValidFormula = isDiscoverySet ? true : Math.abs(totalPersen - 100) < 0.01;
        
        if (member?.role === 'developer') {
          const hasAnyItem = hasBotol || p.tutup_id || p.spray_id || p.is_fullset || (p.racikan && p.racikan.length > 0) || (p.komponen_tambahan && p.komponen_tambahan.length > 0);
          return p.nama_product.trim() && hasAnyItem;
        }
        return p.nama_product.trim() && isValidFormula && hasBotol;
      });

  const canSave = String(namaBrand || '').trim() && isProductsValid && (activeProducts.length > 0 || legalitas.length > 0);

  const sortedProducts = [...activeProducts];

  // Hitung Estimasi PO Hari berdasarkan stok (Real-time)
  const deficits = [];
  const usageMap = {};

  sortedProducts.forEach(p => {
    const qty = parseFloat(String(p.qty || 1).replace(',', '.'));
    if (p.botol_id) {
      const botol = botolList.find(b => b.id === p.botol_id);
      if (botol) {
        usageMap[`botol_${botol.id}`] = usageMap[`botol_${botol.id}`] || { needed: 0, item: botol, tipe: 'Botol' };
        usageMap[`botol_${botol.id}`].needed += qty;
      }
    }
    if (p.tutup_id) {
      const tutup = tutupList.find(t => t.id === p.tutup_id);
      if (tutup) {
        usageMap[`tutup_${tutup.id}`] = usageMap[`tutup_${tutup.id}`] || { needed: 0, item: tutup, tipe: 'Tutup' };
        usageMap[`tutup_${tutup.id}`].needed += qty;
      }
    }
    if (p.spray_id) {
      const spray = sprayList.find(s => s.id === p.spray_id);
      if (spray) {
        usageMap[`spray_${spray.id}`] = usageMap[`spray_${spray.id}`] || { needed: 0, item: spray, tipe: 'Spray' };
        usageMap[`spray_${spray.id}`].needed += qty;
      }
    }

    const botol = p.botol_id ? botolList.find(b => b.id === p.botol_id) : null;
    const botolAktual = p.botol_dari_client ? 0 : (botol?.ukuran_aktual_ml || 0);
    const botolLabel = p.botol_dari_client ? (p.botol_ukuran_client || 0) : (botol?.ukuran_label_ml || 0);
    const hasAktual = botolAktual > 0 && botolAktual !== botolLabel;
    const ukuran = (p.ukuran_digunakan === 'aktual' && hasAktual) ? botolAktual : botolLabel;

    (p.racikan || []).forEach(r => {
      if (!r.bahan_id) return;
      const bahan = bahanList.find(b => b.id === r.bahan_id);
      if (bahan) {
        const vol = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuran * qty;
        usageMap[`bahan_${bahan.id}`] = usageMap[`bahan_${bahan.id}`] || { needed: 0, item: bahan, tipe: 'Cairan' };
        usageMap[`bahan_${bahan.id}`].needed += (bahan.satuan === 'g' ? vol * 0.8 : vol);
      }
    });
  });

  Object.values(usageMap).forEach(({ needed, item, tipe }) => {
    let stok = tipe === 'Cairan' ? (item.stok || 0) * 1000 : (item.stok || 0);
    if (needed > stok) {
      deficits.push({
        nama: item.nama,
        tipe,
      });
    }
  });

  const maxQty = Math.max(0, ...sortedProducts.map(p => parseFloat(String(p.qty || 1).replace(',', '.'))));
  const hasBox = sortedProducts.some(p => (p.komponen_tambahan || []).some(k => 
    (k.kategori || '').toLowerCase().includes('box') ||
    (k.kategori || '').toLowerCase().includes('sablon') ||
    (k.kategori || '').toLowerCase().includes('hot print')
  ));
  let estimasiHariProduksi = 2;
  if (maxQty > 2000) estimasiHariProduksi = 7;
  else if (maxQty >= 1000) estimasiHariProduksi = 5;
  else if (maxQty >= 500) estimasiHariProduksi = 3;
  if (hasBox) estimasiHariProduksi = 15;

  const estimasiTotalHari = estimasiHariProduksi + 2; // +2 buffer internal
  
  const deadlineDate = new Date(tanggal || new Date());
  let daysAdded = 0;
  while (daysAdded < estimasiTotalHari) {
    deadlineDate.setDate(deadlineDate.getDate() + 1);
    if (deadlineDate.getDay() !== 0 && deadlineDate.getDay() !== 6) daysAdded++;
  }

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    
    try {
      const cleanBrand = String(namaBrand || 'BRAND').trim().replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const mDate = new Date(tanggal || new Date());
      const romanMonths = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
      const monthChar = romanMonths[mDate.getMonth()];
      const brandPOsThisMonth = clientPOs.filter(po => {
        const poBrand = (po.customerName || '').split(' / ')[0].trim().replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (poBrand !== cleanBrand) return false;
        const poDateStr = po.date || po.created_date || new Date().toISOString();
        // Parse dd MMM yyyy or ISO
        let poDate = new Date(poDateStr);
        if (isNaN(poDate.getTime())) {
            const parts = poDateStr.split(' ');
            if (parts.length >= 3) {
                poDate = new Date(`${parts[0]} ${parts[1]} ${parts[2]}`);
            }
        }
        if (isNaN(poDate.getTime())) poDate = new Date();
        return poDate.getMonth() === mDate.getMonth() && poDate.getFullYear() === mDate.getFullYear();
      });
      let count = 1;
      let generatedPoId = poMode === 'sph' ? `SPH-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}` : `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
      
      // Fill gaps: keep incrementing until we find an ID that doesn't exist
      while (clientPOs.some(po => po.id === generatedPoId)) {
        count++;
        generatedPoId = `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
        if (poMode === 'sph') {
          generatedPoId = `SPH-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
        }
      }
      
      const finalPoId = editId || generatedPoId;

      // Build Invoice/PO Data
      const newPO = {
        id: finalPoId,
        clientId: 'cli_finance_internal',
        varianLabel: sortedProducts.map(p => `${p.nama_product || 'Varian'} (${String(p.qty).replace(',', '.')} pcs)`).join(', '),
        date: format(new Date(tanggal), 'dd MMM yyyy'),
        statusProduksi: (editId && editPO) 
          ? editPO.statusProduksi 
          : (sortedProducts.length === 0 && legalitas.length > 0 ? 'LEGALITAS SAJA' : 'BELUM NAIK PRODUKSI'),
        statusBayar: (editId && editPO) ? editPO.statusBayar : 'BELUM DIBAYAR',
        isSPH: poMode === 'sph',
        customerName: penerimaKlien?.trim() ? `${namaBrand} / ${penerimaKlien.trim()}` : namaBrand,
        penerima: penerimaKlien?.trim() || '',
        customerPenerima: penerimaKlien?.trim() || '',
        customerAddress: customerAddress || (editId && editPO ? editPO.customerAddress : '-'),
        phone: customerPhone || (editId && editPO ? editPO.phone : '-'),
        courier: (editId && editPO) ? editPO.courier : '-',
        resi: (editId && editPO) ? editPO.resi : '-',
        shippingCost: parseFloat(ongkir) || 0,
        shippingCostLabel: ongkirLabel || 'Ongkos Kirim / Lainnya',
        dpPercentage: (editId && editPO) ? editPO.dpPercentage : 0,
        paymentMethod: selectedBank,
        paymentTerms: paymentTerms,
        tempoDate: paymentTerms === 'tempo' ? (tempoDate ? format(new Date(tempoDate), 'dd MMM yyyy') : null) : null,
        deadline: manualDeadline ? format(new Date(manualDeadline), 'dd MMM yyyy') : format(deadlineDate, 'dd MMM yyyy'),
        ppn_mode: ppnMode,
        ppn_percentage: ppnMode === 'exclude' ? parseFloat(ppnPercentage) || 0 : 0,
        pic: pic || '',
        pic_marketing: pic || '',
        isSample: poMode === 'sample',
        poMode: poMode,
        is_custom_finance: poMode === 'custom_finance',
        items: sortedProducts.map(p => ({
          nama: p.nama_product,
          qty: parseFloat(String(p.qty || 1).replace(',', '.')),
          hargaTotal: p.total_harga_jual || 0,
          hasImage: false
        })),
        products: sortedProducts.map(p => {
          if (poMode === 'custom_finance') {
            return {
              ...p,
              qty: parseFloat(String(p.qty || 1).replace(',', '.')),
              botol_nama: null,
              botol_ukuran_label: 0,
              botol_ukuran_aktual: 0,
              tutup_nama: null,
              spray_nama: null,
              fullset_nama: null,
              racikan: [],
              komponen_tambahan: [],
              jasa_nominal: 0,
              harga_jual_per_pcs: p.harga_jual_per_pcs || 0,
              total_harga_jual: p.total_harga_jual || 0,
              total_hpp_product: p.total_hpp_product || 0,
              hpp_per_pcs_digunakan: p.harga_beli || 0
            };
          }
          const botol = p.botol_dari_client ? null : botolList.find(b => b.id === p.botol_id);
          const tutup = p.tutup_dari_client ? null : tutupList.find(t => t.id === p.tutup_id);
          const spray = p.spray_dari_client ? null : sprayList.find(s => s.id === p.spray_id);
          const fullset = p.is_fullset ? fullsetList.find(f => f.id === p.fullset_id) : null;

          let botolNama = p.botol_dari_client ? 'Botol Dari Client' : (botol?.nama || p.botol_nama || '');
          if (!botol && p.botol_nama && p.botol_nama !== 'Dari Client' && !p.botol_dari_client && !p.is_fullset) {
            botolNama = `${p.botol_nama} (Item database brand tidak ada di inventory)`;
          }

          let tutupNama = p.tutup_dari_client ? null : (tutup?.nama || p.tutup_nama || null);
          if (!tutup && p.tutup_nama && p.tutup_nama !== 'Dari Client' && !p.tutup_dari_client && !p.is_fullset) {
            tutupNama = `${p.tutup_nama} (Item database brand tidak ada di inventory)`;
          }

          let sprayNama = p.spray_dari_client ? null : (spray?.nama || p.spray_nama || null);
          if (!spray && p.spray_nama && p.spray_nama !== 'Dari Client' && !p.spray_dari_client && !p.is_fullset) {
            sprayNama = `${p.spray_nama} (Item database brand tidak ada di inventory)`;
          }

          let fullsetNama = fullset?.nama || p.fullset_nama || null;
          if (p.is_fullset && !fullset && p.fullset_nama && p.fullset_nama !== 'Dari Client') {
            fullsetNama = `${p.fullset_nama} (Item database brand tidak ada di inventory)`;
          }

          return {
            ...p,
            qty: parseFloat(String(p.qty || 1).replace(',', '.')),
            botol_nama: botolNama,
            botol_ukuran_label: p.custom_ukuran_ml !== undefined ? p.custom_ukuran_ml : (p.botol_dari_client ? (p.botol_ukuran_client || 0) : (botol?.ukuran_label_ml || 0)),
            botol_ukuran_aktual: p.custom_ukuran_aktual_ml !== undefined ? p.custom_ukuran_aktual_ml : (p.botol_dari_client ? 0 : (botol?.ukuran_aktual_ml || 0)),
            botol_harga_beli: p.botol_harga_beli || 0,
            botol_harga: p.botol_harga_jual || getHrgJual(botol),
            tutup_nama: tutupNama,
            tutup_harga_beli: p.tutup_harga_beli || 0,
            tutup_harga: p.tutup_harga_jual || getHrgJual(tutup),
            spray_nama: sprayNama,
            spray_harga_beli: p.spray_harga_beli || 0,
            spray_harga: p.spray_harga_jual || getHrgJual(spray),
            fullset_nama: fullsetNama,
            fullset_harga_beli: p.fullset_harga_beli || fullset?.harga_rupiah || 0,
            fullset_harga_jual: p.fullset_harga_jual || getHrgJual(fullset),
            racikan: (p.racikan || []).map(r => ({
              ...r,
              hpp_bahan: r.hpp_bahan || 0
            })),
            komponen_tambahan: (p.komponen_tambahan || []).map(k => {
              const name = k.item_nama || k.nama_item || k.nama;
              const found = poMode === 'database' && !k.dari_client ? itemTambahanList.find(i => i.nama === name) : null;
              return {
                ...k,
                harga: found ? (found.harga_rupiah || 0) : (k.harga || 0),
                harga_jual: found ? (found.harga_jual || 0) : (k.harga_jual || 0)
              };
            }),
            jasa_nominal: p.jasa_nominal !== undefined ? p.jasa_nominal : p.final_jasa_nominal,
            harga_jual_per_pcs: p.harga_jual_per_pcs,
            total_harga_jual: p.total_harga_jual,
            total_hpp_product: p.total_hpp_product,
            hpp_per_pcs_digunakan: p.hpp_per_pcs_digunakan
          };
        }),
        legalitas: ['custom', 'sph', 'custom_finance', 'database'].includes(poMode) ? legalitas.map(l => ({
          nama: l.nama,
          harga_beli: l.harga_beli || 0,
          harga_jual: l.harga_jual || 0,
          qty_variant: l.qty_variant || (l.selected_variants ? l.selected_variants.length : 1),
          selected_variants: l.selected_variants || []
        })) : [],
        timeline: editId && editPO ? editPO.timeline : [
          { status: 'Pesanan Dibuat', date: format(new Date(), 'dd MMM yyyy HH:mm'), desc: 'Invoice Dibuat oleh Finance', done: true, active: true },
          { status: 'Pesanan Dibayar', date: '-', desc: '-', done: false },
          { status: 'Menunggu Produksi', date: '-', desc: '-', done: false },
          { status: 'Proses Produksi', date: '-', desc: '-', done: false },
          { status: 'Pesanan Dikirim', date: '-', desc: '-', done: false },
          { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
        ],
        riwayatPembayaran: editId && editPO ? editPO.riwayatPembayaran : [],
        resiTimeline: editId && editPO ? editPO.resiTimeline : [],
        updated_at: format(new Date(), 'dd MMM yyyy HH:mm'),
        sph_spesifikasi: poMode === 'sph' ? sphSpesifikasi : '',
        sph_tambahan: poMode === 'sph' ? sphTambahan : '',
        sph_terms: poMode === 'sph' ? sphTerms : ''
      };

      if (editId) {
        // Jika sudah pernah naik produksi, beri tanda bahwa ada update yang pending agar bisa diklik tombol "Update PO Produksi"
        const normalizeCode = (str) => String(str || '').replace(/^(PO-|INV-|SPH-)/i, '').trim().toLowerCase();
        const existProdOrder = (productionOrders || []).find(p => 
          p.order_number === editId || 
          (p.notes && p.notes.includes(editId)) || 
          (editPO && (p.order_number === editPO.id || (p.notes && p.notes.includes(editPO.id)))) ||
          (p.order_number && (normalizeCode(p.order_number) === normalizeCode(editId) || (editPO && normalizeCode(p.order_number) === normalizeCode(editPO.id))))
        );
        const isNaikProduksi = Boolean(existProdOrder) || (editPO && editPO.statusProduksi && editPO.statusProduksi !== 'BELUM NAIK PRODUKSI');
        if (isNaikProduksi) {
          newPO.has_pending_po_update = true;
          // Note: we intentionally do not change statusProduksi so it stays in its active stage
        }
        await updatePO(editPO ? editPO.id : editId, newPO);
      } else {
        let success = false;
        let attempts = 0;
        let currentPoId = newPO.id;
        
        while (!success && attempts < 20) {
          try {
            await createPO({ ...newPO, id: currentPoId });
            success = true;
          } catch (err) {
            if (err.message && err.message.toLowerCase().includes('duplicate key')) {
              attempts++;
              count++;
              currentPoId = `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
              // Make sure to skip frontend-known duplicates too
              while (clientPOs.some(po => po.id === currentPoId)) {
                count++;
                currentPoId = `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
              }
            } else {
              throw err;
            }
          }
        }
        if (!success) throw new Error("Sistem mendeteksi terlalu banyak bentrok ID di database, gagal menyimpan.");
      }
      
      setSaved(true);
      toast.success(editId ? 'Berhasil Mengupdate Invoice!' : 'Invoice berhasil dibuat & masuk ke daftar Finance/Marketing!');
    } catch (err) {
      toast.error(editId ? 'Gagal mengupdate invoice: ' + err.message : 'Gagal menyimpan invoice: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="px-3 md:px-0 space-y-6">
      {editId && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col md:flex-row gap-4 justify-between items-center shadow-sm">
          <div>
            <h3 className="text-amber-800 font-bold flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Anda Sedang Dalam Mode Edit Invoice ({editId})
            </h3>
            <p className="text-xs text-amber-700/80 mt-1">Menyimpan perubahan akan memperbarui invoice yang sudah ada, tanpa membuat ID baru.</p>
          </div>
          <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100 whitespace-nowrap" onClick={() => window.history.length > 2 ? window.history.back() : navigate(isMarketing ? '/office/marketing/invoices' : '/office/finance/invoices')}>Batal Edit & Kembali</Button>
        </div>
      )}

      {/* Header (Removed globally to match mockup) */}
      <div className="w-full pb-2 md:pb-0">
        <KursBar />
      </div>

      <div className="flex bg-[#F9F5EC] p-1 rounded-xl mb-6 md:mb-8 w-full max-w-2xl mx-auto border border-[#E6D5B8]">
        <button 
          onClick={() => { setPoMode('database'); setNamaBrand(''); setSearchBrand(''); }}
          className={`flex-1 py-2 text-[11px] md:text-sm font-bold rounded-lg transition-all ${poMode === 'database' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-[#A39171] hover:text-[#8C6208]'}`}
        >
          Repeat Order
        </button>
        <button 
          onClick={() => { setPoMode('custom'); setNamaBrand(''); setSearchBrand(''); }}
          className={`flex-1 py-2 text-[11px] md:text-sm font-bold rounded-lg transition-all ${poMode === 'custom' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-[#A39171] hover:text-[#8C6208]'}`}
        >
          Varian Baru
        </button>
        <button 
          onClick={() => { setPoMode('sample'); setNamaBrand(''); setSearchBrand(''); }}
          className={`flex-1 py-2 text-[11px] md:text-sm font-bold rounded-lg transition-all ${poMode === 'sample' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-[#A39171] hover:text-[#8C6208]'}`}
        >
          Sample
        </button>
        <button 
          onClick={() => { setPoMode('custom_finance'); setNamaBrand(''); setSearchBrand(''); }}
          className={`flex-1 py-2 text-[11px] md:text-sm font-bold rounded-lg transition-all ${poMode === 'custom_finance' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-[#A39171] hover:text-[#8C6208]'}`}
        >
          Custom
        </button>
        <button 
          onClick={() => { setPoMode('sph'); setNamaBrand(''); setSearchBrand(''); }}
          className={`flex-1 py-2 text-[11px] md:text-sm font-bold rounded-lg transition-all ${poMode === 'sph' ? 'bg-white text-[#8C6208] shadow-sm' : 'text-[#A39171] hover:text-[#8C6208]'}`}
        >
          SPH
        </button>
      </div>

      {poMode === 'database' && !namaBrand ? (
        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-[#E6D5B8] max-w-2xl mx-auto mt-10 animate-in fade-in zoom-in-95">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#F9F5EC] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#E6D5B8]">
              <Search className="w-7 h-7 text-[#8C6208]" />
            </div>
            <h2 className="text-3xl font-black text-[#302621] mb-3 tracking-tight">Cari Brand Klien</h2>
            <p className="text-[#7A695B] mb-8 text-sm">Pilih brand klien yang akan dibuatkan pesanan Repeat Order. Seluruh varian yang telah tersimpan di Database akan otomatis ditarik.</p>
          </div>
          
          <BrandSearchDropdown
            brands={allAvailableBrands}
            value={searchBrand}
            onChange={setSearchBrand}
            onSelect={(brand) => setNamaBrand(brand)}
            placeholder="Ketik nama brand..."
          />

          <div className="mt-6">
            <Button 
              onClick={() => {
                if (searchBrand.trim()) {
                  setNamaBrand(searchBrand.trim());
                }
              }}
              disabled={!searchBrand.trim()}
              className="w-full bg-[#8C6208] hover:bg-[#6e4c06] text-white py-6 rounded-xl text-lg font-bold shadow-lg shadow-[#8C6208]/20"
            >
              Lanjutkan Buat Pesanan
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white border border-[#E6D5B8] rounded-xl md:rounded-2xl p-3 md:p-5 shadow-sm">
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h3 className="text-sm md:text-base font-bold text-[#302621] flex items-center gap-2">
                <Package2 className="w-4 h-4 text-[#8C6208]" />
                Informasi Invoice
              </h3>
              {poMode === 'database' && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowScannerModal(true)}
                    className="h-8 border-[#8C6208]/30 bg-[#8C6208]/5 text-[#8C6208] hover:bg-[#8C6208]/10 rounded-lg text-xs font-bold px-3 flex items-center gap-1.5 shadow-2xs"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" />
                    Import PO (AI / Spreadsheet)
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setNamaBrand('');
                      setSearchBrand('');
                      setProducts([createEmptyProduct()]);
                    }}
                    className="h-8 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg text-xs font-bold px-3"
                  >
                    Ganti Brand
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">Nama Brand / Client <span className="text-red-500">*</span></label>
                {poMode === 'database' ? (
                  <div className="h-9 md:h-10 px-3 md:px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                    <span className="text-[11px] md:text-sm font-semibold text-[#302621] truncate pr-4">{String(namaBrand || 'Belum dipilih')}</span>
                  </div>
                ) : (
                  <BrandSearchDropdown
                    brands={Array.from(new Set(
                      clientAccounts
                        .filter(c => {
                          if (c.status !== 'verified') return false;
                          if (c.status === 'deleted' || c.is_deleted) return false;
                          if (c.email === 'satalawebdeveloper@gmail.com') return false;
                          const lower = (c.brand_name || '').toLowerCase();
                          if (['developer', 'satala dev client', 'test'].includes(lower)) return false;
                          return true;
                        })
                        .map(c => c.brand_name)
                        .filter(Boolean)
                    ))}
                    value={namaBrand}
                    onChange={setNamaBrand}
                    onSelect={(brand) => {
                      setNamaBrand(brand);
                    }}
                    placeholder="Contoh: PT Cielmora"
                  />
                )}
              </div>
              <div>
                <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">Tanggal Pembuatan Invoice</label>
                <CustomDatePicker value={tanggal} onChange={setTanggal} className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl" />
              </div>

            </div>

            {/* Informasi Kontak & Alamat Pengiriman Client (Muncul di Repeat Order & Varian Baru sebagai acuan Surat Jalan) */}
            <div className="mb-6 p-4 rounded-xl bg-[#FFFDF9] border border-[#E6D5B8] space-y-3 shadow-2xs">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F0E8D9] pb-2.5">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-[#8C6208]" />
                  <h4 className="text-xs font-bold text-[#302621]">
                    Kontak & Alamat Pengiriman Client (Acuan Surat Jalan)
                  </h4>
                </div>
                <span className="text-[10px] font-bold text-[#8C6208] bg-[#FAF4EB] px-2 py-0.5 rounded border border-[#E6D5B8] flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Otomatis Terhubung ke Surat Jalan & Produksi
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 flex items-center gap-1">
                    <User className="w-3 h-3 text-[#8C6208]" />
                    Nama Owner / Penerima
                  </label>
                  <Input 
                    value={penerimaKlien} 
                    onChange={e => setPenerimaKlien(e.target.value)} 
                    placeholder="Nama Owner / Penerima (Kosong = Nama Brand)" 
                    className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl bg-white" 
                  />
                </div>
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-[#8C6208]" />
                    Nomor Telepon / WhatsApp
                  </label>
                  <Input 
                    value={customerPhone} 
                    onChange={e => setCustomerPhone(e.target.value)} 
                    placeholder="Contoh: 08123456789" 
                    className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl bg-white" 
                  />
                </div>
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[#8C6208]" />
                    Alamat Pengiriman Lengkap
                  </label>
                  <Input 
                    value={customerAddress} 
                    onChange={e => setCustomerAddress(e.target.value)} 
                    placeholder="Jl. Panggulaan Rt.02 Rw.04..." 
                    className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl bg-white" 
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                * Data Owner / Penerima, telepon, dan alamat ini akan tersimpan di Invoice dan otomatis menjadi acuan default pengiriman & Surat Jalan (terpisah dari PIC Marketing Satala).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {poMode !== 'sph' && (
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 flex items-center gap-1">
                    Tanggal Deadline 
                    <span className="text-[9px] font-normal text-slate-400 font-medium">(Opsional, otomatis jika kosong)</span>
                  </label>
                  <CustomDatePicker value={manualDeadline} onChange={setManualDeadline} className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl" />
                </div>
              )}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] md:text-xs font-bold text-[#7A695B] block">Status PPN</label>
                    {selectedBank === 'bca' && (
                      <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                        ASB: Include PPN
                      </span>
                    )}
                    {selectedBank === 'bni' && (
                      <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                        SDE: Exclude PPN (+11%)
                      </span>
                    )}
                  </div>
                  <Select value={ppnMode} onValueChange={setPpnMode}>
                    <SelectTrigger className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl bg-white">
                      <SelectValue placeholder="Pilih Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="include">Include PPN</SelectItem>
                      <SelectItem value="exclude">Exclude PPN (Tambah PPN)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {ppnMode === 'exclude' && (
                  <div className="w-20">
                    <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">Persentase</label>
                    <div className="relative">
                      <Input type="number" value={ppnPercentage} onChange={(e) => setPpnPercentage(e.target.value)} className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl pr-6" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* PIC dan Termin Pembayaran Bebas Dipilih / Diedit untuk Semua Mode Portal */}
              <div className={poMode === 'sph' ? 'md:col-span-2' : ''}>
                <PicSelector
                  label="Marketing Penanggung Jawab (PIC)"
                  labelClassName="text-[10px] md:text-xs"
                  value={pic}
                  onChange={setPic}
                  placeholder="Pilih PIC Marketing untuk Invoice ini..."
                  requiredPageAccessKey="marketing_invoices"
                />
              </div>
              
              {poMode !== 'sph' && (
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">Termin Pembayaran</label>
                  <Select value={paymentTerms} onValueChange={(val) => {
                    setPaymentTerms(val);
                    if (val === 'tempo' && !tempoDate) {
                      const d = new Date(manualDeadline ? manualDeadline : deadlineDate);
                      d.setMonth(d.getMonth() + 1);
                      setTempoDate(format(d, 'yyyy-MM-dd'));
                    }
                  }}>
                    <SelectTrigger className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl bg-white">
                      <SelectValue placeholder="Pilih Termin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lunas">Cash (Tunai / Lunas)</SelectItem>
                      <SelectItem value="dp50">DP 50%, Pelunasan 50%</SelectItem>
                      <SelectItem value="tempo">Jatuh Tempo (Kredit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {poMode !== 'sph' && paymentTerms === 'tempo' && (
                <div className="md:col-span-2">
                  <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">Tanggal Jatuh Tempo</label>
                  <CustomDatePicker value={tempoDate} onChange={setTempoDate} className="h-9 md:h-10 text-[11px] md:text-sm border-[#E6D5B8] rounded-xl" />
                </div>
              )}
            </div>

          {/* Produk Cards */}
          <div className="space-y-4 mt-6">
            {poMode === 'sample' ? (
              <BulkSampleInvoiceForm
                products={products}
                fullsetList={fullsetList}
                bahanList={bahanList}
                bibitSampleList={mappedBibitSampleList}
                onChange={setProducts}
              />
            ) : (
              <>
                {/* Panel Pilih Sekaligus / Batch Repeat Order dari Database Brand */}
                {brandList && brandList.length > 0 && (
                  <BatchRepeatOrderPicker
                    brandList={brandList}
                    brandName={namaBrand}
                    onAddBatchProducts={handleAddBatchProducts}
                    currentProducts={products}
                    itemTambahanList={itemTambahanList}
                  />
                )}

                {products.length === 0 && (
                  <div className="text-center py-8 bg-white border border-dashed border-[#E6D5B8] rounded-2xl">
                    <p className="text-[#8C6208] font-bold">Belum ada Varian Produk</p>
                    <p className="text-xs text-slate-500 mt-1">Anda bisa menyimpan invoice ini hanya dengan Legalitas/BPOM saja, atau klik Tambah Varian di bawah.</p>
                  </div>
                )}
                {products.map((product, originalIdx) => {
                  return poMode === 'custom_finance' ? (
                    <FinanceCustomItemCard
                      key={originalIdx}
                      product={product}
                      index={originalIdx}
                      botolList={botolList}
                      tutupList={tutupList}
                      sprayList={sprayList}
                      bahanList={bahanList}
                      itemTambahanList={itemTambahanList}
                      fullsetList={fullsetList}
                      onChange={(data) => updateProduct(originalIdx, data)}
                      onRemove={() => removeProduct(originalIdx)}
                      canRemove={true}
                    />
                  ) : poMode === 'database' ? (
                    <MarketingProductCard
                      key={originalIdx}
                      product={product}
                      index={originalIdx}
                      botolList={botolList}
                      tutupList={tutupList}
                      sprayList={sprayList}
                      bahanList={bahanList}
                      itemTambahanList={itemTambahanList}
                      fullsetList={fullsetList}
                      varianDatabase={brandList}
                      onChange={handleProductChange}
                      onRemove={() => removeProduct(originalIdx)}
                      canRemove={true}
                    />
                  ) : (
                    <FinanceInvoiceCard
                      key={originalIdx}
                      product={product}
                      index={originalIdx}
                      botolList={botolList}
                      tutupList={tutupList}
                      sprayList={sprayList}
                      bahanList={bahanList}
                      itemTambahanList={itemTambahanList}
                      fullsetList={fullsetList}
                      isSampleMode={false}
                      isDatabaseMode={poMode === 'database'}
                      onChange={(data) => updateProduct(originalIdx, data)}
                      onRemove={() => removeProduct(originalIdx)}
                      onDuplicate={() => duplicateProductPackaging(originalIdx)}
                      canRemove={true}
                    />
                  );
                })}
                {poMode === 'database' ? (
                  <button
                    type="button"
                    onClick={addProduct}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 mt-4 border-2 border-dashed border-[#D4AF37] text-[#8C6208] font-bold rounded-xl hover:bg-[#F9F5EC] transition-all cursor-pointer"
                  >
                    <Plus className="w-5 h-5" /> Tambah Varian Produk Lain (Dari Database)
                  </button>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <button
                      type="button"
                      onClick={addProduct}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 border-2 border-dashed border-[#D4AF37]/80 text-[#8C6208] hover:text-[#302621] font-bold text-xs sm:text-sm rounded-xl bg-[#FFFDF9] hover:bg-[#F4EDE1] transition-all shadow-2xs cursor-pointer group"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#E6D5B8]/60 text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white flex items-center justify-center transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </div>
                      <span>Varian Baru (Form Kosong)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => duplicateProductPackaging()}
                      disabled={products.length === 0}
                      className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 border-2 border-[#D4AF37] bg-gradient-to-r from-[#FFFDF9] via-[#FDF8EE] to-[#F7EEDD] hover:from-[#F4EDE1] hover:to-[#EAE0D0] text-[#8C6208] hover:text-[#302621] font-bold text-xs sm:text-sm rounded-xl transition-all shadow-2xs hover:shadow-xs cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Salin botol, tutup, spray, box/dus, dan jasa dari varian sebelumnya tanpa mengisi nama & bibit"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/30 text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white flex items-center justify-center transition-colors">
                        <Copy className="w-3.5 h-3.5" />
                      </div>
                      <span>Duplikat Kemasan Varian Sebelumnya</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Legalitas */}
          {['custom', 'sph', 'custom_finance', 'database'].includes(poMode) && (
            <FinanceLegalitasSection 
              legalitasList={legalitas} 
              onLegalitasChange={setLegalitas} 
              masterFinanceLegalitas={legalitasListFinance} 
              availableVariants={availableProductVariants}
            />
          )}
          </div>

          {/* Ringkasan Semua Produk & Legalitas */}
          {(activeProducts.length > 0 || legalitas.length > 0) && (
            <div className="bg-gradient-to-br from-[#302621] to-[#1A1411] text-[#FFFDF9] rounded-2xl p-6 shadow-xl mb-12 mt-8">
              <h3 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4 border-b border-white/10 pb-3">
                Ringkasan Tagihan Invoice
              </h3>
              {activeProducts.length > 0 && (
                <div className="space-y-3">
                  {activeProducts.map((p, i) => {
                    const totalPersen = (p.racikan || []).reduce((s, r) => s + (parseFloat(String(r.persentase).replace(',', '.')) || 0), 0);
                    const vol = p.botol_ukuran_client || p.botol_ukuran_label_ml || p.botol_ukuran_aktual || p.ukuran_botol_ml || p.selected_size || p.custom_ukuran_ml || 0;
                    const hasBotol = Boolean(p.botol_id || p.botol_nama || p.fullset_id || p.fullset_nama || p.is_fullset || p.botol_dari_client || vol > 0);
                    const hasAnyItem = hasBotol || p.tutup_id || p.spray_id || p.is_fullset || (p.racikan && p.racikan.length > 0) || (p.komponen_tambahan && p.komponen_tambahan.length > 0);
                    let isProductValid = false;
                    if (p.is_discovery_set) {
                      isProductValid = p.items && p.items.length > 0;
                    } else if (poMode === 'custom_finance') {
                      isProductValid = (p.nama_product && p.nama_product.trim() !== '');
                    } else if (member?.role === 'developer') {
                      isProductValid = (p.nama_product && hasAnyItem);
                    } else {
                      isProductValid = (Math.abs(totalPersen - 100) < 0.01 && hasBotol && p.nama_product);
                    }
                    
                    const botol = p.botol_dari_client ? null : botolList.find(b => b.id === p.botol_id);
                    const ukuranLabel = p.botol_dari_client || (p.is_fullset && !botol) ? (vol || 0) : (botol?.ukuran_label_ml || vol || 0);

                    return (
                      <div key={i} className="flex flex-col gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center text-xs font-bold text-[#302621]">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{p.nama_product || `Produk ${i + 1}`}</p>
                            {ukuranLabel > 0 && !p.is_discovery_set && (
                              <p className="text-[11px] text-white/70 mt-0.5 font-medium">{ukuranLabel} ml</p>
                            )}
                            {!isProductValid && (
                              <div className="text-[11px] text-red-300 flex items-start gap-1.5 mt-1 pr-2">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-[1px]" />
                                <span className="leading-tight whitespace-normal break-words">
                                  {p.is_discovery_set ? 'Item Discovery Set kosong' : (poMode === 'custom_finance' ? 'Nama kosong' : member?.role === 'developer' ? 'Nama kosong atau belum ada item dipilih' : (!hasBotol ? 'Botol belum dipilih' : !p.nama_product ? 'Nama kosong' : `Racikan belum 100%`))}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0 pl-1">
                            <p className="text-[10px] text-white/50">Harga: Rp {fmt(p.final_harga_jual_per_pcs || p.harga_jual_per_pcs || 0)} × {String(p.qty).replace(',', '.')}</p>
                            {isProductValid ? (
                              <p className="font-bold text-white">Rp {fmt(p.total_harga_jual)}</p>
                            ) : (
                              <p className="text-xs opacity-50">—</p>
                            )}
                          </div>
                        </div>
                        
                        {p.is_discovery_set && p.breakdown_items && p.breakdown_items.length > 0 && (
                          <div className="pl-10 pt-2 border-t border-white/10 space-y-1.5">
                            {p.breakdown_items.map((bItem, bIdx) => (
                              <div key={bIdx} className="flex justify-between items-center text-[11px] text-white/60 pr-1">
                                <span>- {bItem.nama}{bItem.ukuran ? ` (${bItem.ukuran}ml)` : ''}</span>
                                <span>Rp {fmt(bItem.jual)} × {String(p.qty).replace(',', '.')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {['custom', 'sph', 'custom_finance', 'database'].includes(poMode) && legalitas.length > 0 && (
                <div className={`${activeProducts.length > 0 ? 'border-t border-white/20 mt-4 pt-4' : ''} space-y-2`}>
                  {legalitas.map((l, i) => {
                    const hargaJualSatuan = l.harga_jual !== undefined && l.harga_jual !== '' ? l.harga_jual : 0;
                    const qty = l.qty_variant || 1;
                    const hargaJualTotal = hargaJualSatuan * qty;
                    return (
                      <div key={i} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                        <div className="flex flex-col min-w-0">
                          <span className="opacity-90 font-medium">
                            {l.nama}{qty > 1 ? ` (${qty} varian)` : ''}
                          </span>
                          {qty > 1 && (
                            <span className="text-[11px] text-amber-200/90 font-medium mt-0.5">
                              Total: Rp {fmt(hargaJualTotal)}
                            </span>
                          )}
                          {Array.isArray(l.selected_variants) && l.selected_variants.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              <span className="text-[10px] text-amber-300/80 font-semibold">Varian:</span>
                              {l.selected_variants.map((v, vIdx) => (
                                <span key={vIdx} className="text-[10px] bg-amber-500/20 text-amber-200 px-2 py-0.5 rounded-md border border-amber-500/30">
                                  ✓ {v}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2.5 w-full sm:w-auto">
                          {/* Qty Input */}
                          <div className="flex items-center gap-1.5 bg-black/25 px-2.5 py-1.5 rounded-lg border border-white/20">
                            <span className="text-xs text-white/70 font-semibold">Qty:</span>
                            <input
                              type="number"
                              min="1"
                              value={qty || 1}
                              onChange={(e) => {
                                const newQty = Math.max(1, parseInt(e.target.value) || 1);
                                setLegalitas(prev => prev.map(item => item.nama === l.nama ? { ...item, qty_variant: newQty } : item));
                              }}
                              className="w-12 text-xs font-bold text-white bg-transparent border-none p-0 text-center focus:ring-0"
                            />
                          </div>

                          {/* Unit Price Input */}
                          <div className="text-right w-36 sm:w-44 relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-white/70 font-semibold">Rp</span>
                            <Input
                              type="number"
                              value={hargaJualSatuan || ''}
                              onChange={(e) => {
                                const newUnit = parseFloat(e.target.value) || 0;
                                setLegalitas(prev => prev.map(item => item.nama === l.nama ? { ...item, harga_jual: newUnit } : item));
                              }}
                              className="h-9 pl-9 bg-black/20 border-white/20 text-white font-bold text-right focus-visible:ring-[#D4AF37] rounded-lg text-xs"
                              placeholder="0"
                            />
                          </div>

                          {/* Total Badge */}
                          <div className="text-right min-w-[100px] bg-white/5 px-2.5 py-1 rounded-lg border border-white/10">
                            <span className="text-[9px] text-white/60 block leading-none">Total</span>
                            <span className="text-xs text-amber-300 font-extrabold">
                              Rp {fmt(hargaJualTotal)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {poMode === 'sample' && (
                <div className="bg-[#241d18] rounded-xl p-5 border border-white/10 space-y-2 mt-6">
                  {(() => {
                    const totalKemasanSample = products.reduce((acc, p) => acc + ((p.hpp_kemasan || 0) * (parseFloat(String(p.qty || 1).replace(',', '.')))), 0);
                    const totalCairanSample = products.reduce((acc, p) => acc + ((p.hpp_cairan || 0) * (parseFloat(String(p.qty || 1).replace(',', '.')))), 0);
                    const totalJasaSample = products.reduce((acc, p) => {
                      const jasaPcs = (p.hpp_satuan || 0) - (p.hpp_kemasan || 0) - (p.hpp_cairan || 0);
                      return acc + (jasaPcs * (parseFloat(String(p.qty || 1).replace(',', '.'))));
                    }, 0);
                    const totalModalSample = products.reduce((acc, p) => acc + ((p.hpp_satuan || 0) * (parseFloat(String(p.qty || 1).replace(',', '.')))), 0);
                    const grandTotalSample = products.reduce((acc, p) => acc + (p.total_harga_jual || 0), 0);
                    const totalMarginSample = grandTotalSample - totalModalSample;
                    const marginPercentage = totalModalSample > 0 ? ((totalMarginSample / totalModalSample) * 100).toFixed(1) : 0;

                    return (
                      <>
                        <div className="flex justify-between text-sm text-white/80 items-center">
                          <span className="flex items-center gap-2"><Package className="w-4 h-4 text-[#D4AF37]" /> Total Modal Kemasan (Packaging)</span>
                          <span className="font-medium">Rp {fmt(totalKemasanSample)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-white/80 items-center">
                          <span className="flex items-center gap-2"><Droplets className="w-4 h-4 text-blue-400" /> Total Modal Formula Cairan</span>
                          <span className="font-medium">Rp {fmt(totalCairanSample)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-white/80 items-center">
                          <span className="flex items-center gap-2"><Settings className="w-4 h-4 text-slate-400" /> Total Biaya Jasa Produksi</span>
                          <span className="font-medium">Rp {fmt(totalJasaSample)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-bold text-[#D4AF37] border-t border-white/10 pt-3 mt-2 items-center">
                          <span className="flex items-center gap-2"><Wallet className="w-4 h-4" /> Total Harga Sistem / Modal</span>
                          <span>Rp {fmt(totalModalSample)}</span>
                        </div>
                        <div className="bg-[#1A251A] border border-[#2E412E] rounded-xl p-5 mt-5 flex flex-col md:flex-row justify-between items-center shadow-inner gap-4">
                          <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                              <TrendingUp className="w-6 h-6 text-emerald-400" />
                            </div>
                            <div>
                              <span className="text-emerald-400/80 text-[10px] font-bold uppercase tracking-widest block mb-1">Total Margin Profit</span>
                              <span className="text-emerald-400 font-black text-xl sm:text-2xl leading-none block">+Rp {fmt(totalMarginSample)}</span>
                              <span className="text-emerald-500/70 text-[10px] font-medium block mt-1">{marginPercentage}% dari Total Modal</span>
                            </div>
                          </div>
                          <div className="w-full md:w-auto md:text-right border-t md:border-t-0 border-white/10 pt-4 md:pt-0">
                            <span className="text-white/60 text-[10px] font-bold uppercase tracking-widest block mb-1 flex items-center md:justify-end gap-1">
                              <Receipt className="w-3 h-3" /> Grand Total Penjualan
                            </span>
                            <span className="text-[#D4AF37] font-black text-2xl sm:text-3xl leading-none block">Rp {fmt(grandTotalSample)}</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {poMode !== 'sample' && (
                <>
                  <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-center bg-white/5 px-4 py-3 rounded-xl border border-white/10">
                    <Input
                      type="text"
                      value={ongkirLabel}
                      onChange={(e) => setOngkirLabel(e.target.value)}
                      className="h-8 max-w-[250px] bg-transparent border-white/10 text-white font-medium focus-visible:ring-[#D4AF37] opacity-90 px-2"
                      placeholder="Ongkos Kirim / Lainnya"
                    />
                    <div className="w-32 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white font-bold">Rp</span>
                      <Input
                        type="number"
                        value={ongkir || ''}
                        onChange={(e) => setOngkir(e.target.value)}
                        className="h-9 pl-9 bg-black/20 border-white/20 text-white font-bold text-right focus-visible:ring-[#D4AF37] rounded-lg"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  {ppnMode === 'exclude' && (
                    <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-center text-sm bg-white/5 px-4 py-2 rounded-xl border border-white/10 text-white/80">
                      <span>PPN Tambahan ({ppnPercentage || 0}%)</span>
                      <span className="font-bold">Rp {fmt(nominalPPN)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-center">
                    <span className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider">Grand Total Invoice</span>
                    <span className="text-3xl font-black text-white">Rp {fmt(totalHargaJualSemua)}</span>
                  </div>
                </>
              )}
            </div>
          )}



          {/* Action Buttons */}
          <div className="mt-6 md:mt-8 mb-12 border border-[#E6D5B8] bg-white rounded-xl p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              
              <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                <Button variant="outline" onClick={handleReset} type="button" className="w-[100px] shrink-0 h-12 gap-2 text-[#7A695B] border-[#E6D5B8] hover:bg-[#F9F5EC]">
                  <RotateCcw className="w-4 h-4" /> <span className="hidden sm:inline">Reset</span>
                </Button>
                
                {canEdit && !saved && poMode !== 'sph' && (
                  <div className="flex-1 min-w-0 sm:min-w-[180px]">
                    <Select 
                      value={selectedBank} 
                      onValueChange={(bankVal) => {
                        setSelectedBank(bankVal);
                        const clientPpnMode = matchedClient?.default_ppn_mode || 'auto';
                        if (clientPpnMode === 'auto') {
                          if (bankVal === 'bni') {
                            setPpnMode('exclude');
                            setPpnPercentage(11);
                          } else if (bankVal === 'bca') {
                            setPpnMode('include');
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="h-12 bg-white border-[#E6D5B8] w-full">
                        <div className="flex items-center gap-2 truncate">
                          <Landmark className="w-4 h-4 text-[#8C6208] shrink-0" />
                          <span className="truncate">
                            <SelectValue placeholder="Pilih Rekening" />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(accountsMap || {}).map(([key, acc]) => (
                          <SelectItem key={key} value={key}>{acc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {canEdit && !saved && (
                <Button
                  onClick={handleSave}
                  type="button"
                  disabled={!canSave || isSaving}
                  className={`w-full sm:flex-1 h-12 gap-2 text-white text-sm font-bold shadow-lg ${editId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#302621] hover:bg-[#1A1411]'}`}
                >
                  <Save className="w-5 h-5 shrink-0" />
                  <span className="truncate">{isSaving ? (editId ? 'Mengupdate...' : 'Menyimpan...') : (editId ? 'Update & Simpan Perubahan' : (poMode === 'sph' ? 'Buat SPH Baru' : 'Buat Invoice & Masukkan ke Finance'))}</span>
                </Button>
              )}

              {saved && (
                <Button disabled type="button" className="flex-1 h-12 gap-2 bg-green-700 text-white text-sm font-bold shadow-lg opacity-100">
                  <CheckCircle2 className="w-5 h-5" />
                  {editId ? 'Update Berhasil Tersimpan' : (poMode === 'sph' ? 'Tersimpan ke Daftar SPH' : 'Tersimpan ke Finance')}
                </Button>
              )}

              {saved && (
                <Button type="button" variant="outline" onClick={() => navigate(poMode === 'sph' ? (isMarketing ? '/office/marketing/sph-list' : '/office/finance/sph-list') : (isMarketing ? '/office/marketing/invoices' : '/office/finance/invoices'))} className="h-12 gap-2 border-[#D4AF37] text-[#8C6208] hover:bg-[#F9F5EC] px-6">
                  {poMode === 'sph' ? 'Lihat di Daftar SPH' : 'Lihat di Invoices'} <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {showScannerModal && (
        <RepeatOrderScannerModal
          isOpen={showScannerModal}
          onClose={() => setShowScannerModal(false)}
          namaBrand={namaBrand}
          varianDatabase={brandList}
          botolList={botolList}
          tutupList={tutupList}
          sprayList={sprayList}
          bahanList={bahanList}
          itemTambahanList={itemTambahanList}
          fullsetList={fullsetList}
          onApply={(scannedProducts) => {
            setProducts(scannedProducts);
          }}
        />
      )}
    </div>
  );
}